/**
 * Player Element Components — re-export hub.
 * Individual elements are in their own files for maintainability.
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { ElementIcon, LearningStyleBadge, getLearningStyle } from './shared';

// Import all element components
import VideoElement from './VideoElement';
import QuizElement from './QuizElement';
import CaseStudyElement from './CaseStudyElement';
import FlashcardElement from './FlashcardElement';
import FillGapsElement from './FillGapsElement';
import DragDropElement from './DragDropElement';
import MatchingElement from './MatchingElement';
import ImageHotspotElement from './ImageHotspotElement';
import AudioElement from './AudioElement';
import DownloadableElement from './DownloadableElement';
import ReflectionJournalElement from './ReflectionJournalElement';
import TextElement from './TextElement';
import TrainerTipElement from './TrainerTipElement';
import TimelineElement from './TimelineElement';
import LabelDiagramElement from './LabelDiagramElement';
import SortOrderElement from './SortOrderElement';
import DecisionTreeElement from './DecisionTreeElement';
import ProgressCheckpointElement from './ProgressCheckpointElement';
import { ElementErrorBoundary } from './ElementErrorBoundary';

// Re-export shared utilities
export { ElementIcon, LearningStyleBadge, getLearningStyle };

// Element Renderer Dispatcher
export const ElementRenderer = ({ element, progress, onComplete, onSaveData, onSubmitQuiz, isCompleted, courseId, api, currentModuleIdx, onModuleRestart }) => {
  const config = element.config || {};
  const typeConfig = config[element.type] || config;

  const renderers = {
    video: VideoElement, quiz: QuizElement, case_study: CaseStudyElement,
    flashcard: FlashcardElement, fill_gaps: FillGapsElement, drag_drop_task: DragDropElement,
    matching: MatchingElement, image_hotspot: ImageHotspotElement, audio: AudioElement,
    downloadable: DownloadableElement, reflection_journal: ReflectionJournalElement, text: TextElement,
    trainer_tip: TrainerTipElement,
    timeline: TimelineElement,
    label_diagram: LabelDiagramElement,
    sort_order: SortOrderElement,
    decision_tree: DecisionTreeElement,
    progress_checkpoint: ProgressCheckpointElement,
  };

  const Renderer = renderers[element.type] || TextElement;

  return (
    <div data-testid={`element-${element.id}`}>
      <div className="flex items-center gap-3 mb-4">
        <ElementIcon type={element.type} className="h-5 w-5 text-[#20acf6]" />
        <h2 className="text-xl font-bold text-[#2a3e5d]">{element.title}</h2>
        <LearningStyleBadge type={element.type} />
        {isCompleted && <Badge className="bg-green-100 text-green-700 text-xs">Completed</Badge>}
      </div>
      <ElementErrorBoundary key={element.id}>
      <Renderer
        config={typeConfig} element={element} progress={progress}
        onComplete={onComplete} onSaveData={onSaveData} onSubmitQuiz={onSubmitQuiz}
        isCompleted={isCompleted} currentModuleIdx={currentModuleIdx} onModuleRestart={onModuleRestart}
      />
      </ElementErrorBoundary>
    </div>
  );
};
